import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { MapPin, RefreshCcw, Cloud, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'bestbuddy_geo';

/**
 * Weather-aware Nanouk tips for the Nutrition / Fitness pages.
 *
 * Props:
 *   category: 'nutrition' | 'fitness'  → which tip set to show
 *
 * Reads the user's coordinates via the browser Geolocation API, caches them
 * for 12h in localStorage (so we don't ask every page load). Then calls
 * `/api/weather/tips` which derives tailored tips from temperature, humidity
 * and weather code. Entirely rule-based — zero LLM cost.
 */
export const WeatherTipsCard = ({ category = 'nutrition' }) => {
    const { language, t: _t } = useLanguage();
    const [loading, setLoading] = useState(true);
    const [weather, setWeather] = useState(null);
    const [tips, setTips] = useState([]);
    const [error, setError] = useState(null);
    const [needsPermission, setNeedsPermission] = useState(false);

    const isDe = language === 'de';

    const readCachedCoords = () => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            const age = Date.now() - (parsed.ts || 0);
            if (age > 12 * 60 * 60 * 1000) return null; // 12h
            return parsed.coords || null;
        } catch { return null; }
    };

    const cacheCoords = (coords) => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ ts: Date.now(), coords }));
        } catch { /* ignore */ }
    };

    const fetchTips = useCallback(async (coords) => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await axios.get(`${API}/weather/tips`, {
                params: { lat: coords.lat, lon: coords.lon, category, language },
            });
            if (data?.error) {
                setError(isDe ? 'Wetter nicht erreichbar' : 'Weather unavailable');
                setTips([]); setWeather(null);
            } else {
                setWeather(data.weather);
                setTips(data.tips || []);
            }
        } catch {
            setError(isDe ? 'Wetter-Tipps konnten nicht geladen werden' : 'Weather tips could not be loaded');
        } finally {
            setLoading(false);
        }
    }, [category, language, isDe]);

    const requestLocation = useCallback(() => {
        if (!navigator.geolocation) {
            setError(isDe ? 'Browser unterstützt keinen Standort' : 'Browser does not support geolocation');
            setLoading(false);
            return;
        }
        setNeedsPermission(false);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const coords = { lat: pos.coords.latitude, lon: pos.coords.longitude };
                cacheCoords(coords);
                fetchTips(coords);
            },
            (err) => {
                setLoading(false);
                if (err.code === 1) {
                    setNeedsPermission(true);
                } else {
                    setError(isDe ? 'Standort konnte nicht ermittelt werden' : 'Location unavailable');
                }
            },
            { enableHighAccuracy: false, timeout: 7000, maximumAge: 60 * 60 * 1000 }
        );
    }, [fetchTips, isDe]);

    useEffect(() => {
        const cached = readCachedCoords();
        if (cached) {
            fetchTips(cached);
        } else {
            requestLocation();
        }
    }, [fetchTips, requestLocation]);

    if (loading) {
        return (
            <Card data-testid={`weather-tips-${category}`}>
                <CardContent className="p-4 flex items-center gap-3 text-sm text-muted-foreground">
                    <Cloud className="w-5 h-5 animate-pulse" />
                    {isDe ? 'Wetter-Tipps werden geladen...' : 'Loading weather tips...'}
                </CardContent>
            </Card>
        );
    }

    if (needsPermission) {
        return (
            <Card data-testid={`weather-tips-${category}`}>
                <CardContent className="p-4 flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-semibold">
                            {isDe ? 'Standort für Wetter-Tipps freigeben' : 'Allow location for weather tips'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                            {isDe
                                ? 'Nanouk gibt dir basierend aufs aktuelle Wetter passende Ernährungs- und Trainings-Tipps.'
                                : 'Nanouk gives you tailored nutrition and training tips based on current weather.'}
                        </p>
                        <Button size="sm" className="mt-2" onClick={requestLocation} data-testid={`weather-tips-${category}-permission-btn`}>
                            {isDe ? 'Standort freigeben' : 'Allow location'}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card data-testid={`weather-tips-${category}`}>
                <CardContent className="p-4 flex items-center gap-3 text-sm">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    <span className="flex-1 text-muted-foreground">{error}</span>
                    <Button size="sm" variant="outline" onClick={requestLocation}>
                        <RefreshCcw className="w-3.5 h-3.5" />
                    </Button>
                </CardContent>
            </Card>
        );
    }

    if (!tips.length) return null;

    const peakToday = weather?.peak_today ?? weather?.temperature;
    const isHeat = peakToday >= 28;
    const isCold = peakToday < 5;
    const headerTone = isHeat
        ? 'from-orange-500/15 to-red-500/15 border-orange-200'
        : isCold
            ? 'from-sky-500/15 to-indigo-500/15 border-sky-200'
            : 'from-emerald-500/10 to-teal-500/10 border-emerald-200';

    // Surface the day-peak prominently when "now" is mild but day will heat up
    const heatingUp = weather && weather.temperature < 25 && weather.day_max >= 28;
    const greeting = isHeat
        ? (heatingUp
            ? (isDe ? `🐺 Jetzt ${weather.temperature}°C – aber bis zu ${weather.day_max}°C heute! Bereite dich vor.` : `🐺 ${weather.temperature}°C now – but up to ${weather.day_max}°C today! Prep ahead.`)
            : (isDe ? `🐺 Hitze-Tag bei ${weather.temperature}°C – pass auf dich auf!` : `🐺 Heat day at ${weather.temperature}°C – take care!`))
        : isCold
            ? (isDe ? `🐺 Kalt heute bei ${weather.temperature}°C – ich hab ein paar Wärmer-Tipps für dich.` : `🐺 Cold today at ${weather.temperature}°C – here are some warm-you-up tips.`)
            : (isDe ? `🐺 ${weather.temperature}°C ${weather.label} – perfekte Bedingungen.` : `🐺 ${weather.temperature}°C ${weather.label} – great conditions.`);

    return (
        <Card data-testid={`weather-tips-${category}`} className="overflow-hidden">
            <div className={`bg-gradient-to-br ${headerTone} border-b px-4 py-3 flex items-center gap-3`}>
                <div className="text-2xl" aria-hidden="true">{weather?.emoji}</div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold leading-tight" data-testid="weather-tips-greeting">{greeting}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        {category === 'nutrition'
                            ? (isDe ? 'Ernährungs-Tipps für heute' : 'Nutrition tips for today')
                            : category === 'sleep'
                                ? (isDe ? 'Schlaf-Tipps für heute Nacht' : 'Sleep tips for tonight')
                                : (isDe ? 'Trainings-Tipps für heute' : 'Training tips for today')}
                    </p>
                </div>
                <Button size="icon" variant="ghost" onClick={requestLocation} data-testid={`weather-tips-${category}-refresh`} aria-label="Refresh">
                    <RefreshCcw className="w-4 h-4" />
                </Button>
            </div>
            <CardContent className="p-0 divide-y">
                {tips.map((tip, i) => (
                    <div key={i} className="flex items-start gap-3 px-4 py-3" data-testid={`weather-tip-${category}-${i}`}>
                        <div className="text-xl flex-shrink-0">{tip.icon}</div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold flex items-center gap-1.5">
                                {tip.title}
                                {tip.severity === 'high' && (
                                    <span className="text-[10px] uppercase tracking-wide font-bold text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
                                        {isDe ? 'Wichtig' : 'Important'}
                                    </span>
                                )}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{tip.body}</p>
                        </div>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
};
